import type { ChipTone } from '../../components/Chip';
import type { QrState, QrSummary } from './types';

export const STATE_ORDER: QrState[] = ['generado', 'impreso', 'en_custodia', 'activado', 'suspendido', 'dado_de_baja'];

export const STATE_LABEL: Record<QrState, string> = {
  generado: 'Generado',
  impreso: 'Impreso',
  en_custodia: 'En custodia',
  activado: 'Activado',
  suspendido: 'Suspendido',
  dado_de_baja: 'Dado de baja',
};

/** Punto de color del KPI de inventario (design-spec v3). */
export const STATE_DOT: Record<QrState, string> = {
  generado: '#3F434C',
  impreso: '#A5A49D',
  en_custodia: '#16181D',
  activado: '#2E9E4F',
  suspendido: '#C2410C',
  dado_de_baja: '#6B6E75',
};

const STATE_TONE: Record<QrState, ChipTone> = {
  generado: 'neutral',
  impreso: 'neutral',
  en_custodia: 'premium',
  activado: 'success',
  suspendido: 'warning',
  dado_de_baja: 'neutral',
};

export function stateChip(qr: Pick<QrSummary, 'state' | 'suspension' | 'artStatus'>): { label: string; tone: ChipTone } {
  if (qr.suspension?.security) return { label: 'Bloqueado por seguridad', tone: 'error' };
  if (qr.artStatus === 'generando') return { label: 'Generando', tone: 'art' };
  if (!qr.state) return { label: 'Sin estado', tone: 'neutral' };
  return { label: STATE_LABEL[qr.state], tone: STATE_TONE[qr.state] };
}

export const KIND_LABEL: Record<string, string> = {
  physical: 'Físico (lote)',
  digital: 'Digital',
  legacy: 'Integración',
};

export function typeLabel(qr: Pick<QrSummary, 'kind' | 'type' | 'qrType' | 'qrStyle'>): string {
  if (qr.type === 'art' || qr.qrType === 'art') return 'QR Art';
  if (qr.kind === 'physical') return 'Físico (lote)';
  if (qr.type === 'styled' || qr.qrType === 'styled' || qr.qrStyle) return 'Con estilo';
  return 'Clásico';
}

export interface Reason {
  value: string;
  label: string;
}

/** Motivos de suspensión (R4.8). */
export const SUSPEND_REASONS: Reason[] = [
  { value: 'falta_de_pago', label: 'Falta de pago del cliente' },
  { value: 'pedido_cliente', label: 'Pedido del cliente' },
  { value: 'contenido_en_revision', label: 'Contenido en revisión' },
  { value: 'otro', label: 'Otro' },
];

/** Motivos de baja (R4.7). */
export const RETIRE_REASONS: Reason[] = [
  { value: 'perdido', label: 'Perdido' },
  { value: 'danado', label: 'Dañado' },
  { value: 'robado', label: 'Robado' },
  { value: 'otro', label: 'Otro' },
];

export const EXCLUSION_LABEL: Record<string, string> = {
  dado_de_baja: 'dado de baja',
  already_activated: 'ya activado',
  activado: 'ya activado',
  not_found: 'no existe',
  other_org: 'de otra organización',
  invalid_state: 'estado no permite el cambio',
  invalid_code: 'código inválido',
  skipped: 'omitido',
  not_in_lot: 'no es de este lote',
  state_activado: 'ya activado',
  state_generado: 'aún no impreso',
  already_in_custody: 'ya estaba con ese custodio',
};

export function exclusionText(e: { code?: string; id?: string; reason?: string; state?: string | null }): string {
  const code = e.code ?? e.id?.split(':').pop() ?? '—';
  const fromState = (s?: string | null) => (s && STATE_LABEL[s as QrState] ? STATE_LABEL[s as QrState].toLowerCase() : undefined);
  const reason =
    (e.reason && EXCLUSION_LABEL[e.reason]) ??
    (e.reason?.startsWith('state_') ? fromState(e.reason.slice(6)) : undefined) ??
    fromState(e.state) ??
    e.reason ??
    'omitido';
  return `${code} ${reason}`;
}

export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatNumber(n?: number | null): string {
  return (n ?? 0).toLocaleString('es-CL');
}

const ROLE_LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', editor: 'Editor', viewer: 'Lector', seller: 'Vendedor', operator: 'Operador' };

export function roleLabel(role?: string | null): string {
  return role ? (ROLE_LABEL[role] ?? role) : '';
}

export function memberLabel(m: { name?: string | null; email?: string | null; uid: string; role?: string | null }): string {
  const who = m.name ?? m.email ?? m.uid;
  return m.role ? `${who} (${roleLabel(m.role)})` : who;
}
